import { msgState } from "components";
import { SField, SFieldProps } from "components/parts";
import { getFormError } from "messages";
import { useEffect } from "react";
import {
  useController,
  UseControllerProps,
  useFormContext,
} from "react-hook-form";
import NumberFormat from "react-number-format";
import { useSetRecoilState } from "recoil";

/**
 * 数値フィールド
 */

/**
 * プロパティ
 */
type Props = Pick<UseControllerProps, "name" | "rules"> &
  Partial<{
    size: number;
    scale: number;
    code: boolean;
  }> &
  Pick<
    SFieldProps,
    | "focusId"
    | "label"
    | "required"
    | "index"
    | "align"
    | "gridColumn"
    | "gridRow"
    | "disabled"
    | "dialog"
    | "handleSubmit"
    | "onBlur"
    | "onChange"
    | "onKeyDown"
    | "onClick"
    | "onDoubleClick"
  >;

/**
 * 数値フィールド
 *
 * @param name: フィールド名
 * @param rules: react-hook-formのルールを設定(必須はrequiredで指定する)
 * @param size: 最大桁数(小数点含む)
 * @param scale:sizeの内、小数点部分の桁数
 * @param code: コード値(先頭0入力可能)
 * @param label: エラーメッセージ用のラベル
 * @param required: 必須
 * @param index: フォーカス番号(1～)
 * @param align: 文字位置
 * @param gridColumn: CSSグリッドの列位置を指定する
 * @param gridRow: CSSグリッドの行位置を指定する
 * @param disabled: 非活性
 * @param dialog: ダイアログ表示画面を指定する
 * @param handleSubmit: 元画面への通知関数
 * @returns JSX.Element
 */
export const SNumberField = ({
  name,
  rules,
  size,
  scale,
  code,
  label,
  required,
  ...props
}: Props): JSX.Element => {
  // フォーム設定
  const { control, trigger } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
    rules: {
      ...rules,
      required: required && {
        value: required,
        message: getFormError("required", label ?? ""),
      },
    },
  });

  // エラーメッセージ設定
  const setMsg = useSetRecoilState(msgState);

  useEffect(() => {
    setMsg(fieldState.error?.message ?? "");
  });

  return (
    <NumberFormat
      {...props}
      type="tel"
      name={field.name}
      value={field.value}
      formRef={field.ref}
      error={fieldState.invalid}
      onBlur={(event: any) => {
        trigger(name);
        setMsg(fieldState.error?.message ?? "");
        props.onBlur?.call(this, event);
      }}
      onChange={(event: any) => {
        field.onChange(event);
        props.onChange?.call(this, event);
      }}
      decimalScale={code ? 0 : scale}
      thousandSeparator={props.disabled && !code}
      allowLeadingZeros={code}
      allowNegative={!code}
      thousandsGroupStyle="thousand"
      isNumericString
      customInput={SField}
      isAllowed={(values) => {
        const length = values.value.replace(".", "").length;
        if (typeof size === "undefined") return true;
        if (typeof size === "number") return length <= size;
        return false;
      }}
    />
  );
};
